import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { Button, Card, Col, Container, Form, Row } from "react-bootstrap";
import { PencilIcon } from '@primer/octicons-react'
import { SearchIcon } from '@primer/octicons-react'
import LocPickerDialog from './LocPickerDialog';
import { axiosAuth_DATA_MGMT_API } from '../utils/AxoisUtilComponent';
import { useTranslation } from 'react-i18next';
import classes from './AdminPages.module.css';
import { translateAPIError } from '../utils/apiUtils';
import { formatDate } from '../utils/timeUtils';


const ModelObjAdminDetailsView = (props) => {

  // Utilities
  const { t } = useTranslation(['common', 'custom']);
  const navigate = useNavigate();

  // Initialize with actual JSON obj.  If not, will get error.
  const [objAttrs, setObjAttrs] = useState({
    id: '',
    objType: '',
    bimGuid: '',
    bimElementId: '',
    assetCode: '',
    locationCode: '',
    locationId: '',
    locationName: '',
    floor: ''
  });

  const [currentObjAttrs,          setCurrentObjAttrs]         = useState([]);


  // View vs Edit Modes
  const [editMode, setEditMode] = useState(false);
  const [errMsg,   setErrMsg]   = useState('');


  // UI Functions

  // Initialize state
  useEffect(() => {
    if (props) {
      fetchObj(props.objDetails.id);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props]);


  const fetchObj = (id) => {

    return new Promise(async (resolve, reject) => {
      try {
        const res = await axiosAuth_DATA_MGMT_API.get("model-obj-mappings/" + id);

        resolve(res.data);

        if (res.status === 200) {
          setObjAttrs(res.data[0]);
          setCurrentObjAttrs(res.data[0]);
        }

        setErrMsg('');
      } catch (error) {
        setErrMsg(t(translateAPIError(error)));
      }
    });
  }


  const handleEditAttrs = (e) => {
    e.preventDefault();
    setEditMode(true);
  }


  const onAttrsInputChange = (e) => {
    setObjAttrs({ ...objAttrs, [e.target.name]: e.target.value })
  }

  const handleEditCancel = (e) => {
    e.preventDefault();

    // Reset fields
    setObjAttrs(currentObjAttrs);

    setEditMode(false);
    setErrMsg('');
  }

  const handleEditSubmit = (e) => {
    e.preventDefault();

    // Validate form inputs
    if (!objAttrs.modelElemId || objAttrs.modelElemId.trim().length === 0) {
      setErrMsg(t("I18N_ERR_Need_model_elem_id"));
      return;
    } else if (objAttrs.modelElemId.length > 30) {
      setErrMsg(t("I18N_ERR_Max_len_model_elem_id") + 30);
      return;
    } else if (!objAttrs.objId || objAttrs.objId.trim().length === 0) {
      setErrMsg(t("I18N_ERR_Need_obj_id"));
      return;
    } else if (objAttrs.objId.length > 30) {
      setErrMsg(t("I18N_ERR_Max_len_obj_id") + 30);
      return;
    } else if (!objAttrs.objType || objAttrs.objType.trim().length === 0) {
      setErrMsg(t("I18N_ERR_Need_obj_type"));
      return;
    } else if (objAttrs.objType.length > 20) {
      setErrMsg(t("I18N_ERR_Max_len_obj_type") + 20);
      return;
    } else if (objAttrs.bimGuid && objAttrs.bimGuid.length > 50) {
      setErrMsg(t("I18N_ERR_Max_len_obj_bim_guid") + 50);
      return;
    } else if (objAttrs.bimElementId && objAttrs.bimElementId.length > 50) {
      setErrMsg(t("I18N_ERR_Max_len_obj_bim_elem_id") + 50);
      return;
    }

    const reqBody = objAttrs;

    // Note: reqBody.locationCode is actually not used by the API
    if (reqBody.locationCode === '') {
      reqBody.locationId = null;
    }

    return new Promise(async (resolve, reject) => {
      try {
        const res = await axiosAuth_DATA_MGMT_API.put("model-obj-mappings/" + objAttrs.id, reqBody);

        resolve(res.data);

        if (res.status === 200) {

          // Restore UI to viewing mode
          setEditMode(false);
          setErrMsg('');

          fetchObj(objAttrs.id);
        }
      } catch (error) {
        if (!error.message || (error.message !== 'IGNORE')) {
          setErrMsg(t(translateAPIError(error)));
        }
      }
    });

  }


  const handleClose = (e) => {
    e.preventDefault();

    // Return to list
    navigate(-1);
  }


  // Location picker dialog stuff

  const [showLocPickerDialog, setShowLocPickerDialog] = useState(false);

  const handleLocPicker = (e) => {
    setShowLocPickerDialog(true);
  }

  function handleLocPickerClose() {
    setShowLocPickerDialog(false);
  }

  function handleLocSelect(location) {

    // Deep copy
    let newAttrs = JSON.parse (JSON.stringify(objAttrs));
    newAttrs.locationId = location.id;
    newAttrs.locationCode   = location.code;

    setObjAttrs(newAttrs);

    setShowLocPickerDialog(false);
  }


  return (
    <>
      <Card style={{overflow:'auto', border:'0', boxShadow:'0 2px 2px 0px rgba(0,0,0,0.1)',
                    padding:'20px 40px 20px 40px', maxWidth:'2500px', width:'100%', fontSize:'13px'}}>

        <div style={{display:'flex'}}>
        <span className={classes.admin_pages__page_title}>
          {t("I18N_ModelObjMapping_Details")}
        </span>
        </div>

        <div className={classes.admin_pages__page_subtitle} style={{marginTop:'20px'}}>
          <div display='flex'>
            <span>
              {t("I18N_ModelObjMapping_info")}
            </span>
            {editMode ?
              null
              :
              <span style={{marginLeft:'15px', fontSize:'9px', color:'SteelBlue', cursor:'pointer'}} onClick={handleEditAttrs}>
                <PencilIcon size={12} />
                {t("I18N_Edit")}
              </span>
            }
          </div>
          <hr className="solid" style={{ margin: '0 0 10px 0' }}></hr>

          {editMode ?
            <Container>
              <form>
                <Row className={classes.admin_pages__field_label}>
                  <Col md={3}>{t("I18N_Model_elem_id")}: *</Col>
                  <Col md={3}>{t("I18N_Obj_id")}: *</Col>
                  <Col md={3}>{t("I18N_Obj_type")}: *</Col>
                  <Col md={3}>{t("I18N_Location_code")}: </Col>
                </Row>
                <Row className={classes.admin_pages__field_value}>
                  <Col md={3}>
                    <input className={classes.admin_pages__field_editable} style={{width:'100%'}} name="modelElemId" value={objAttrs.modelElemId}
                      onChange={(e) => onAttrsInputChange(e)} />
                  </Col>
                  <Col md={3}>
                    <input className={classes.admin_pages__field_editable} style={{width:'100%'}} name="objId" value={objAttrs.objId}
                      onChange={(e) => onAttrsInputChange(e)} />
                  </Col>
                  <Col md={3}>
                    <input className={classes.admin_pages__field_editable} style={{width:'100%'}} name="objType" value={objAttrs.objType}
                      onChange={(e) => onAttrsInputChange(e)} />
                  </Col>
                  <Col md={3}>
                    <input readOnly className={classes.admin_pages__field_editable} style={{minWidth: '85%'}} name="location" value={objAttrs.locationCode}
                      onChange={(e) => onAttrsInputChange(e)} />
                    <span onClick={handleLocPicker} style={{ marginLeft:'10px', cursor: 'pointer', color: 'rgb(100,100,100)' }}>
                      <SearchIcon size={14} />
                    </span>
                  </Col>
                </Row>

                <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                  <Col md={3}>{t("I18N_BIM_GUID")}:</Col>
                  <Col md={3}>{t("I18N_BIM_Element_id")}:</Col>
                  <Col md={3}></Col>
                  <Col md={3}></Col>
                </Row>
                <Row className={classes.admin_pages__field_value}>
                  <Col md={3}>
                    <textarea className={classes.admin_pages__field_editable} style={{width:'100%'}} name="bimGuid" value={objAttrs.bimGuid}
                      onChange={(e) => onAttrsInputChange(e)} />
                  </Col>
                  <Col md={3}>
                    <input className={classes.admin_pages__field_editable} style={{width:'100%'}} name="bimElementId" value={objAttrs.bimElementId}
                      onChange={(e) => onAttrsInputChange(e)} />
                  </Col>
                  <Col md={3}></Col>
                  <Col md={3}></Col>
                </Row>

                <Row>
                  <div display='flex' style={{marginTop:'15px'}}>
                    <Button size="tiny" variant="outline-secondary" className="mb-3"
                      style={{ float: 'left' }}
                      onClick={handleEditCancel}>
                      {t("I18N_Cancel")}
                    </Button>
                    <Button size="tiny" variant="outline-secondary" className="mb-3"
                      style={{ marginLeft: '15px' }}
                      onClick={handleEditSubmit}>
                      {t("I18N_Save")}
                    </Button>
                    <span className={classes.admin_pages__field_label} style={{marginLeft:'30px', color:'rgba(200,0,0)', verticalAlign:'top'}}>
                      {errMsg}
                    </span>
                  </div>
                </Row>
              </form>
            </Container>
            :
            <Container>
              <Row className={classes.admin_pages__field_label}>
                <Col md={3}>{t("I18N_Model_elem_id")}:</Col>
                <Col md={3}>{t("I18N_Obj_id")}:</Col>
                <Col md={3}>{t("I18N_Obj_type")}:</Col>
                <Col md={3}>{t("I18N_Location_code")}:</Col>
              </Row>
              <Row className={classes.admin_pages__field_value}>
                <Col md={3}>{objAttrs.modelElemId}</Col>
                <Col md={3}>{objAttrs.objId}</Col>
                <Col md={3}>{objAttrs.objType}</Col>
                <Col md={3}>{objAttrs.locationCode}</Col>
              </Row>

              <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                <Col md={3}>{t("I18N_BIM_GUID")}:</Col>
                <Col md={3}>{t("I18N_BIM_Element_id")}:</Col>
                <Col md={3}></Col>
                <Col md={3}></Col>
              </Row>
              <Row className={classes.admin_pages__field_value}>
                <Col md={3}>{objAttrs.bimGuid}</Col>
                <Col md={3}>{objAttrs.bimElementId}</Col>
                <Col md={3}></Col>
                <Col md={3}></Col>
              </Row>

              <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                <Col>{t("I18N_Created_by")}:</Col>
                <Col>{t("I18N_Time_created")}:</Col>
                <Col>{t("I18N_Updated_by")}:</Col>
                <Col>{t("I18N_Time_updated")}:</Col>
              </Row>
              <Row className={classes.admin_pages__field_value}>
                <Col style={{overflowWrap:'anywhere'}}>{objAttrs.creatorUsername}</Col>
                <Col>{formatDate(objAttrs.timeCreated)}</Col>
                <Col style={{overflowWrap:'anywhere'}}>{objAttrs.updaterUsername}</Col>
                <Col>{formatDate(objAttrs.timeUpdated)}</Col>
              </Row>
            </Container>
          }
        </div>


        <hr className="solid" style={{ marginTop: '30px' }}></hr>

        <div className={classes.admin_pages__page_subtitle} >
          <Form.Group as={Row} className="mb-2 mt-3">
            <div style={{ display: "flex" }}>
              <Button size="sm" variant="outline-secondary" className="mb-3"
                style={{ fontSize: '12px', fontWeight: '300' }}
                onClick={handleClose}>
                {t("I18N_Close")}
              </Button>
            </div>
{/*
              <span className={classes.admin_pages__field_label} style={{ color: 'rgba(200,0,0)', verticalAlign: 'top' }}>
                {updateModelObjErrMsg}
              </span>
*/}
          </Form.Group>
        </div>


        <LocPickerDialog
          show={showLocPickerDialog}
          handleObjSelect={handleLocSelect}
          onClose={handleLocPickerClose}
        />

      </Card>
    </>
  );
}

export default ModelObjAdminDetailsView;
